import {
    Alert,
    Badge,
    Button,
    Card,
    Group,
    Loader,
    Modal,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconDatabase, IconUserPlus, IconWorld} from '@tabler/icons-react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useEffect, useMemo, useState} from 'react'

import {
    deactivateOrganization,
    deactivateUser,
    getOrganizationDetail,
    inviteOrganizationUser,
    listOrganizationUsers,
    reactivateOrganization,
    reactivateUser,
    updateOrganization,
} from 'src/api/admin/api'
import {OrganizationDetail} from 'src/api/admin/models/organization'
import GiftTableDisplay from 'src/components/admin/gift-table-display'
import {
    numberInputToNumber,
    OrganizationSettingsFormFields,
    type OrganizationSettingsFormValues,
    organizationSettingsValidators,
} from 'src/components/admin/organization-settings-form'
import LinkButton from 'src/components/ui/link-button'
import {formatCurrency} from 'src/lib/format'

type EditOrganizationModalProps = {
    org: OrganizationDetail
    opened: boolean
    onClose: () => void
    onSubmit: (values: {
        name?: string
        campaignGoal: number
        toplinePercentage: number
        useRfmForEngagement: boolean
    }) => void
    loading: boolean
}

function EditOrganizationModal({
    org,
    opened,
    onClose,
    onSubmit,
    loading,
}: EditOrganizationModalProps) {
    const formOptions = useMemo(
        () => ({
            initialValues: {
                name: org.name,
                campaignGoal: org.campaignGoal,
                toplinePercentage: org.toplinePercentage,
                engagementBasis: (org.useRfmForEngagement ? 'rfm' : 'mlr') as 'mlr' | 'rfm',
            } satisfies OrganizationSettingsFormValues,
            validate: organizationSettingsValidators,
        }),
        [org],
    )

    const form = useForm<OrganizationSettingsFormValues>(formOptions)

    useEffect(() => {
        if (opened) {
            form.setValues({
                name: org.name,
                campaignGoal: org.campaignGoal,
                toplinePercentage: org.toplinePercentage,
                engagementBasis: org.useRfmForEngagement ? 'rfm' : 'mlr',
            })
        }
    }, [opened, org])

    const handleSubmit = form.onSubmit((values) => {
        onSubmit({
            name: values.name.trim(),
            campaignGoal: numberInputToNumber(values.campaignGoal),
            toplinePercentage: numberInputToNumber(values.toplinePercentage),
            useRfmForEngagement: values.engagementBasis === 'rfm',
        })
    })

    return (
        <Modal
            title={`Edit: ${org.name}`}
            opened={opened}
            onClose={() => {
                form.reset()
                onClose()
            }}
            size="md"
        >
            <form onSubmit={handleSubmit}>
                <Stack>
                    <OrganizationSettingsFormFields form={form} />
                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={loading}>
                            Save Changes
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}

type InviteOrgUserFormProps = {
    onSubmit: (values: {email: string; name: string}) => void
    onCancel: () => void
}

function InviteOrgUserForm({onSubmit, onCancel}: InviteOrgUserFormProps) {
    const form = useForm({
        initialValues: {email: '', name: ''},
        validate: {
            email: (value) =>
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') ? null : 'Enter a valid email',
            name: (value) => (value?.trim() ? null : 'Enter a name'),
        },
    })

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TextInput
                    label="Email"
                    placeholder="you@example.com"
                    {...form.getInputProps('email')}
                />
                <TextInput label="Name" placeholder="Full Name" {...form.getInputProps('name')} />
                <Group>
                    <Button variant="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit">Send Invitation</Button>
                </Group>
            </Stack>
        </form>
    )
}

export const Route = createFileRoute('/_authed/admin/organizations/$orgId/')({
    component: AdminOrganizationDetailPage,
})

function AdminOrganizationDetailPage() {
    const {orgId} = useParams({from: '/_authed/admin/organizations/$orgId'})
    const queryClient = useQueryClient()
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [inviteModalOpen, setInviteModalOpen] = useState(false)
    const [deactivateOrgModalOpen, setDeactivateOrgModalOpen] = useState(false)
    const [reactivateOrgModalOpen, setReactivateOrgModalOpen] = useState(false)
    const [memberReactivateTarget, setMemberReactivateTarget] = useState<{
        id: string
        name: string
    } | null>(null)

    const {data: org, isLoading} = useQuery({
        queryKey: ['admin', 'organizations', orgId],
        queryFn: () => getOrganizationDetail(orgId),
    })

    const {data: users = [], isLoading: usersLoading} = useQuery({
        queryKey: ['admin', 'organizations', orgId, 'users'],
        queryFn: () => listOrganizationUsers(orgId),
    })

    const updateMutation = useMutation({
        mutationFn: (body: {
            name?: string
            campaignGoal: number
            toplinePercentage: number
            useRfmForEngagement?: boolean
        }) => updateOrganization(orgId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'organizations']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            setEditModalOpen(false)
            notifications.show({color: 'green', message: 'Organization updated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const deactivateUserMutation = useMutation({
        mutationFn: (userId: string) => deactivateUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId, 'users'],
            })
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            notifications.show({color: 'green', message: 'User deactivated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const deactivateOrgMutation = useMutation({
        mutationFn: () => deactivateOrganization(orgId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'organizations']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            setDeactivateOrgModalOpen(false)
            notifications.show({
                color: 'green',
                message: 'Organization deactivated',
            })
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const reactivateOrgMutation = useMutation({
        mutationFn: () => reactivateOrganization(orgId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'organizations']})
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId, 'users'],
            })
            setReactivateOrgModalOpen(false)
            notifications.show({
                color: 'green',
                message: 'Organization reactivated',
            })
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    const reactivateUserMutation = useMutation({
        mutationFn: (targetUserId: string) => reactivateUser(targetUserId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId, 'users'],
            })
            queryClient.invalidateQueries({
                queryKey: ['admin', 'organizations', orgId],
            })
            setMemberReactivateTarget(null)
            notifications.show({color: 'green', message: 'User reactivated'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    async function inviteOrgUser(values: {email: string; name: string}) {
        await inviteOrganizationUser(orgId, values)
        queryClient.invalidateQueries({
            queryKey: ['admin', 'organizations', orgId, 'users'],
        })
        queryClient.invalidateQueries({
            queryKey: ['admin', 'organizations', orgId],
        })
        queryClient.invalidateQueries({queryKey: ['admin', 'organizations']})
        setInviteModalOpen(false)
        notifications.show({color: 'green', message: 'Invitation sent'})
    }

    if (isLoading || !org) {
        return <Loader size="lg" style={{display: 'block', margin: '100px auto'}} />
    }

    if (org.deactivatedAt) {
        return (
            <>
                <Stack gap="md">
                    <Alert color="red" title="This organization has been deactivated.">
                        <Text size="sm">
                            {
                                'Members cannot sign in until you reactivate this organization. Reactivating restores member access; users whose sessions were revoked must sign in again.'
                            }
                        </Text>
                    </Alert>
                    <Group>
                        <Button onClick={() => setReactivateOrgModalOpen(true)}>
                            Reactivate organization
                        </Button>
                        <LinkButton to="/admin/organizations" variant="default">
                            Back to Organizations
                        </LinkButton>
                    </Group>
                </Stack>
                <Modal
                    title="Reactivate Organization"
                    opened={reactivateOrgModalOpen}
                    onClose={() => setReactivateOrgModalOpen(false)}
                >
                    <Stack>
                        <Text size="sm">
                            Are you sure you want to reactivate {org.name}? All members of this
                            organization will be able to log in again.
                        </Text>
                        <Group>
                            <Button
                                variant="default"
                                onClick={() => setReactivateOrgModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                loading={reactivateOrgMutation.isPending}
                                onClick={() => reactivateOrgMutation.mutate()}
                            >
                                Reactivate
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </>
        )
    }

    return (
        <Stack gap="lg">
            <Group justify="space-between">
                <Title order={3}>{org.name}</Title>
                <Group>
                    <LinkButton
                        to="/organizations/$orgId"
                        params={{orgId}}
                        variant="default"
                        leftSection={<IconWorld size={16} />}
                    >
                        View as Client
                    </LinkButton>
                    <LinkButton
                        to="/admin/organizations/$orgId/data"
                        params={{orgId}}
                        leftSection={<IconDatabase size={16} />}
                    >
                        Manage Data
                    </LinkButton>
                </Group>
            </Group>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>Organization Info</Text>
                </Card.Section>
                <Card.Section inheritPadding py="sm">
                    <Stack gap="xs">
                        <Text size="sm">
                            <Text span fw={500}>
                                Created:
                            </Text>{' '}
                            {new Date(org.createdAt).toLocaleDateString()}
                        </Text>
                        <Text size="sm">
                            <Text span fw={500}>
                                Users:
                            </Text>{' '}
                            {org.userCount}
                        </Text>
                        <Text size="sm">
                            <Text span fw={500}>
                                Prospect records:
                            </Text>{' '}
                            {org.prospectCount}
                        </Text>
                        <Text size="sm">
                            <Text span fw={500}>
                                Campaign goal:
                            </Text>{' '}
                            {formatCurrency(org.campaignGoal)}
                        </Text>
                        <Text size="sm" data-testid="engagement-level-basis">
                            <Text span fw={500}>
                                Engagement Level calculated from:
                            </Text>{' '}
                            {org.useRfmForEngagement ? 'RFM Total' : 'MLR Score'}
                        </Text>
                        <Text size="sm">
                            <Text span fw={500}>
                                Topline percentage:
                            </Text>{' '}
                            {`${org.toplinePercentage}%`}
                        </Text>
                        <Group mt="sm">
                            <Button
                                variant="subtle"
                                size="compact-sm"
                                onClick={() => setEditModalOpen(true)}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="subtle"
                                color="red"
                                size="compact-sm"
                                onClick={() => setDeactivateOrgModalOpen(true)}
                            >
                                Deactivate Organization
                            </Button>
                        </Group>
                    </Stack>
                </Card.Section>
            </Card>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Text fw={500}>Gift Table</Text>
                </Card.Section>
                <Card.Section inheritPadding pt="sm" pb="sm">
                    <Stack gap="sm">
                        <GiftTableDisplay rows={org.giftTableRows} />
                    </Stack>
                </Card.Section>
            </Card>

            <Card withBorder padding="md">
                <Card.Section withBorder inheritPadding py="sm">
                    <Group justify="space-between">
                        <Text fw={500}>Users</Text>
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            leftSection={<IconUserPlus size={14} />}
                            onClick={() => setInviteModalOpen(true)}
                        >
                            Invite User
                        </Button>
                    </Group>
                </Card.Section>
                <Card.Section inheritPadding pt="sm">
                    {usersLoading ? (
                        <Loader size="sm" />
                    ) : users.length === 0 ? (
                        <Alert mb="sm">
                            <Group gap="sm">
                                <Text size="sm">
                                    No users have been invited to this organization.
                                </Text>
                                <Button
                                    variant="subtle"
                                    size="compact-sm"
                                    leftSection={<IconUserPlus size={14} />}
                                    onClick={() => setInviteModalOpen(true)}
                                >
                                    Invite a User
                                </Button>
                            </Group>
                        </Alert>
                    ) : (
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Email</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {users.map((row) => (
                                    <Table.Tr key={row.id}>
                                        <Table.Td>{row.name}</Table.Td>
                                        <Table.Td>{row.email}</Table.Td>
                                        <Table.Td>
                                            <Badge color={row.deactivatedAt ? 'red' : 'green'}>
                                                {row.deactivatedAt ? 'Deactivated' : 'Active'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            {row.deactivatedAt ? (
                                                <Button
                                                    variant="subtle"
                                                    size="compact-sm"
                                                    onClick={() =>
                                                        setMemberReactivateTarget({
                                                            id: row.id,
                                                            name: row.name,
                                                        })
                                                    }
                                                >
                                                    Reactivate
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="subtle"
                                                    color="red"
                                                    size="compact-sm"
                                                    loading={deactivateUserMutation.isPending}
                                                    onClick={() =>
                                                        deactivateUserMutation.mutate(row.id)
                                                    }
                                                >
                                                    Deactivate
                                                </Button>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    )}
                </Card.Section>
            </Card>

            <EditOrganizationModal
                org={org}
                opened={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSubmit={updateMutation.mutate}
                loading={updateMutation.isPending}
            />

            <Modal
                title={`Invite user to ${org.name}`}
                opened={inviteModalOpen}
                onClose={() => setInviteModalOpen(false)}
            >
                <InviteOrgUserForm
                    onSubmit={inviteOrgUser}
                    onCancel={() => setInviteModalOpen(false)}
                />
            </Modal>

            <Modal
                title="Deactivate Organization"
                opened={deactivateOrgModalOpen}
                onClose={() => setDeactivateOrgModalOpen(false)}
            >
                <Stack>
                    <Text size="sm">
                        Are you sure you want to deactivate {org.name}? All users in this
                        organization will be deactivated and logged out.
                    </Text>
                    <Group>
                        <Button variant="default" onClick={() => setDeactivateOrgModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            loading={deactivateOrgMutation.isPending}
                            onClick={() => deactivateOrgMutation.mutate()}
                        >
                            Deactivate
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                title="Reactivate User"
                opened={memberReactivateTarget !== null}
                onClose={() => setMemberReactivateTarget(null)}
            >
                <Stack>
                    <Text size="sm">
                        {memberReactivateTarget
                            ? `Are you sure you want to reactivate ${memberReactivateTarget.name}? They will be able to log in again.`
                            : null}
                    </Text>
                    <Group>
                        <Button variant="default" onClick={() => setMemberReactivateTarget(null)}>
                            Cancel
                        </Button>
                        <Button
                            loading={reactivateUserMutation.isPending}
                            onClick={() => {
                                if (memberReactivateTarget) {
                                    reactivateUserMutation.mutate(memberReactivateTarget.id)
                                }
                            }}
                        >
                            Reactivate
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    )
}
